import React, { useCallback, useMemo, useRef, useState } from "react";
import { createLink, type Link } from "@meshconnect/web-link-sdk";
import styled from "styled-components";
import type { MeshRuntime, MeshLinkSessionCallbacks, MeshMfaRequest, MeshConfig } from "@orb-labs/orby-react";
import { BULLRUN_COLORS } from "@/theme";
import { ENV } from "@/config/env";

// =============================================================================
// MFA Modal — lifted verbatim from the deleted MeshProvider, app-local styling
// =============================================================================

interface MfaModalProps {
  visible: boolean;
  mfaCode: string;
  mfaType: string | null;
  brokerName: string;
  submitting: boolean;
  onCodeChange: (code: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

function MfaModal({
  visible,
  mfaCode,
  mfaType,
  brokerName,
  submitting,
  onCodeChange,
  onSubmit,
  onCancel,
}: MfaModalProps) {
  if (!visible) return null;

  const exchangeName = brokerName || "your exchange";
  let description: string;
  if (mfaType === "totp") {
    description = `Enter the code from ${exchangeName}.`;
  } else if (mfaType === "phone" || mfaType === "sms") {
    description = `Enter the code for ${exchangeName}.`;
  } else if (mfaType === "email") {
    description = `Enter the code sent to your email for ${exchangeName}.`;
  } else {
    description = `Enter your verification code for ${exchangeName}.`;
  }

  return (
    <MfaOverlay>
      <MfaBackdrop onClick={onCancel} />
      <MfaContainer>
        <MfaTitle>Verification Required</MfaTitle>
        <MfaDescription>{description}</MfaDescription>
        <MfaInput
          type="text"
          inputMode="numeric"
          placeholder="Verification code"
          value={mfaCode}
          onChange={(e) => onCodeChange(e.target.value)}
          maxLength={8}
          autoFocus
          disabled={submitting}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSubmit();
          }}
        />
        <MfaVerifyButton onClick={onSubmit} disabled={submitting || mfaCode.length < 4}>
          {submitting ? "Verifying..." : "Verify"}
        </MfaVerifyButton>
        <MfaCancelButton onClick={onCancel}>Cancel</MfaCancelButton>
      </MfaContainer>
    </MfaOverlay>
  );
}

const MfaOverlay = styled.div({
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
});

const MfaBackdrop = styled.div({
  position: "absolute",
  inset: 0,
  background: "rgba(0, 0, 0, 0.5)",
});

const MfaContainer = styled.div({
  position: "relative",
  width: "100%",
  maxWidth: "480px",
  background: "#1A1A1A",
  borderTopLeftRadius: "20px",
  borderTopRightRadius: "20px",
  padding: "32px 24px",
  zIndex: 1,
});

const MfaTitle = styled.h2({
  fontSize: "24px",
  fontWeight: 700,
  color: BULLRUN_COLORS.textPrimary,
  margin: "0 0 8px",
});

const MfaDescription = styled.p({
  fontSize: "14px",
  fontWeight: 500,
  color: BULLRUN_COLORS.textTertiary,
  margin: "0 0 24px",
});

const MfaInput = styled.input({
  width: "100%",
  background: "#2A2A2A",
  borderRadius: "12px",
  border: "none",
  padding: "16px",
  fontSize: "16px",
  color: BULLRUN_COLORS.textPrimary,
  marginBottom: "16px",
  boxSizing: "border-box",
  outline: "none",
  fontFamily: "inherit",
  "::placeholder": { color: BULLRUN_COLORS.textTertiary },
  "&:disabled": { opacity: 0.6 },
});

const MfaVerifyButton = styled.button({
  all: "unset",
  display: "block",
  width: "100%",
  textAlign: "center",
  padding: "14px 0",
  fontSize: "16px",
  fontWeight: 600,
  borderRadius: "12px",
  cursor: "pointer",
  fontFamily: "inherit",
  color: BULLRUN_COLORS.black,
  background: BULLRUN_COLORS.accentPrimary,
  boxSizing: "border-box",
  "&:hover": { opacity: 0.85 },
  "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
});

const MfaCancelButton = styled.button({
  all: "unset",
  display: "block",
  width: "100%",
  textAlign: "center",
  padding: "12px 0",
  marginTop: "8px",
  fontSize: "16px",
  fontWeight: 500,
  color: BULLRUN_COLORS.textTertiary,
  cursor: "pointer",
  fontFamily: "inherit",
});

// =============================================================================
// Hook
// =============================================================================

export interface UseWebMeshRuntimeResult {
  runtime: MeshRuntime;
  /** Render this inside the React tree (portal for MFA modal). */
  MeshHost: React.FC;
}

export function useWebMeshRuntime(): UseWebMeshRuntimeResult {
  const linkRef = useRef<Link | null>(null);

  // MFA state
  const [mfaVisible, setMfaVisible] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaType, setMfaType] = useState<string | null>(null);
  const [mfaBrokerName, setMfaBrokerName] = useState("");
  const [mfaSubmitting, setMfaSubmitting] = useState(false);
  const mfaResolveRef = useRef<((code: string) => void) | null>(null);
  const mfaRejectRef = useRef<((error: Error) => void) | null>(null);

  const handleMfaSubmit = useCallback(() => {
    if (!mfaCode || mfaCode.length < 4) return;
    setMfaSubmitting(true);
    try {
      setMfaVisible(false);
      mfaResolveRef.current?.(mfaCode);
    } finally {
      setMfaSubmitting(false);
      setMfaCode("");
      setMfaType(null);
      setMfaBrokerName("");
      mfaResolveRef.current = null;
      mfaRejectRef.current = null;
    }
  }, [mfaCode]);

  const handleMfaCancel = useCallback(() => {
    setMfaVisible(false);
    setMfaCode("");
    setMfaType(null);
    setMfaBrokerName("");
    mfaRejectRef.current?.(new Error("MFA cancelled by user"));
    mfaResolveRef.current = null;
    mfaRejectRef.current = null;
  }, []);

  const config: MeshConfig = useMemo(
    () => ({
      clientId: ENV.MESH_CLIENT_ID,
      clientSecret: ENV.MESH_CLIENT_SECRET,
      apiBaseUrl: ENV.MESH_SANDBOX === "true" ? ENV.MESH_SANDBOX_API_URL : ENV.MESH_API_URL,
    }),
    []
  );

  const openMeshLinkSession = useCallback(
    (linkToken: string, callbacks: MeshLinkSessionCallbacks) => {
      let integrationConnected = false;
      const link = createLink({
        clientId: config.clientId,
        onIntegrationConnected: (payload) => {
          // The web SDK fires onIntegrationConnected when the broker token
          // exchange completes, then shows a success screen. When the user
          // clicks "Done", the SDK fires onExit. Because our
          // handleIntegrationConnected is async (API calls + encrypted
          // storage), onExit can fire while it's still running — resolving
          // the connection promise prematurely with "cancelled by user" and
          // preventing the account from being saved.
          //
          // Mobile avoids this by unmounting the Link component before
          // invoking the callback (so onExit never fires). On web we can't
          // unmount the SDK's iframe, but we can suppress the onExit that
          // follows a successful integration.
          integrationConnected = true;
          callbacks.onIntegrationConnected(payload);
        },
        onTransferFinished: () => {},
        onExit: (error) => {
          if (integrationConnected) {
            // Integration was already connected — this onExit is just the
            // user clicking "Done" on the success screen (or the SDK tearing
            // down after closeLink). The shared provider's
            // handleIntegrationConnected is already processing; firing
            // handleExit would race and resolve the promise as "cancelled".
            linkRef.current = null;
            return;
          }
          callbacks.onExit(error);
        },
        theme: "dark",
      });
      linkRef.current = link;
      link.openLink(linkToken);
    },
    [config.clientId]
  );

  const closeMeshLinkSession = useCallback(() => {
    if (linkRef.current) {
      linkRef.current.closeLink();
      linkRef.current = null;
    }
  }, []);

  const requestMeshMfaCode = useCallback(
    (request: MeshMfaRequest): Promise<string> =>
      new Promise<string>((resolve, reject) => {
        mfaResolveRef.current = resolve;
        mfaRejectRef.current = reject;
        setMfaType(request.mfaType);
        setMfaBrokerName(request.brokerName);
        setMfaCode("");
        setMfaVisible(true);
      }),
    []
  );

  const subscribeToAppResume = useCallback((listener: () => void): (() => void) => {
    const handler = () => {
      if (document.visibilityState === "visible") {
        listener();
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  const runtime: MeshRuntime = useMemo(
    () => ({
      config,
      openMeshLinkSession,
      closeMeshLinkSession,
      requestMeshMfaCode,
      subscribeToAppResume,
    }),
    [config, openMeshLinkSession, closeMeshLinkSession, requestMeshMfaCode, subscribeToAppResume]
  );

  // Store MFA state in a ref so MeshHost's component identity stays stable
  // across re-renders. Without this, useMemo would create a new function
  // reference on every mfaCode change → React unmounts/remounts the modal
  // on every keystroke, losing focus and causing visible flicker.
  const mfaStateRef = useRef({
    visible: mfaVisible,
    code: mfaCode,
    type: mfaType,
    brokerName: mfaBrokerName,
    submitting: mfaSubmitting,
    onCodeChange: setMfaCode,
    onSubmit: handleMfaSubmit,
    onCancel: handleMfaCancel,
  });
  mfaStateRef.current = {
    visible: mfaVisible,
    code: mfaCode,
    type: mfaType,
    brokerName: mfaBrokerName,
    submitting: mfaSubmitting,
    onCodeChange: setMfaCode,
    onSubmit: handleMfaSubmit,
    onCancel: handleMfaCancel,
  };

  // Stable component identity — never changes, reads current state from ref.
  const [MeshHost] = useState(
    () =>
      function WebMeshHost() {
        // Force re-render when MFA state changes by reading from the hook's state.
        // The parent (MeshBridge) re-renders on state change, which re-renders
        // WebMeshHost, which reads the latest values from the ref.
        const s = mfaStateRef.current;
        return (
          <MfaModal
            visible={s.visible}
            mfaCode={s.code}
            mfaType={s.type}
            brokerName={s.brokerName}
            submitting={s.submitting}
            onCodeChange={s.onCodeChange}
            onSubmit={s.onSubmit}
            onCancel={s.onCancel}
          />
        );
      }
  );

  return { runtime, MeshHost };
}
